import { useRef, useState } from 'react';
import { Download, Share2, Lock, Sparkles, Linkedin, MessageCircle, Instagram, Check, User } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';

/* ============================================================
 * WellnessChampion
 * ------------------------------------------------------------
 * Score >= 70 → Celebration + premium shareable badge
 * Score 60–69 → "Near Champion" progress card + locked badge
 * Score 40–59 → "Building Momentum" progress card + locked badge
 * Score 0–39  → "Beginning Your Wellness Journey" + locked badge
 *
 * Badge is rendered as inline SVG so it can be downloaded as PNG
 * via the Canvas API (no external dependency).
 * ============================================================ */

export function getStage(score) {
  if (score >= 70) return { key: 'champion',     emoji: '🏆', titleKey: 'champion.stage.champion.title', bodyKey: 'champion.stage.champion.body', unlocked: true };
  if (score >= 60) return { key: 'near',         emoji: '⭐', titleKey: 'champion.stage.near.title',     bodyKey: 'champion.stage.near.body',     unlocked: false };
  if (score >= 40) return { key: 'building',     emoji: '🌿', titleKey: 'champion.stage.building.title', bodyKey: 'champion.stage.building.body', unlocked: false };
  return                  { key: 'beginning',    emoji: '🌱', titleKey: 'champion.stage.beginning.title',bodyKey: 'champion.stage.beginning.body',unlocked: false };
}

/* ---------------- Badge SVG (1080×1080 — Instagram square) ---------------- */
function BadgeSVG({ score, name = '', idSuffix = 'live' }) {
  const W = 1080, H = 1080;
  // Clean and truncate name for safe SVG rendering
  const cleanName = (name || '').trim().slice(0, 30);
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height="100%"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block' }}
    >
      <defs>
        <linearGradient id={`bg-${idSuffix}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor="#0F0F23" />
          <stop offset="45%"  stopColor="#1E1B4B" />
          <stop offset="100%" stopColor="#312E81" />
        </linearGradient>
        <radialGradient id={`glow-${idSuffix}`} cx="50%" cy="40%" r="55%">
          <stop offset="0%"   stopColor="#A855F7" stopOpacity="0.45" />
          <stop offset="60%"  stopColor="#6366F1" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#6366F1" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`ring-${idSuffix}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor="#A855F7" />
          <stop offset="50%"  stopColor="#6366F1" />
          <stop offset="100%" stopColor="#4F46E5" />
        </linearGradient>
        <linearGradient id={`text-grad-${idSuffix}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"  stopColor="#FFFFFF" />
          <stop offset="100%" stopColor="#E0E7FF" />
        </linearGradient>
        <linearGradient id={`name-grad-${idSuffix}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"  stopColor="#FBBF24" />
          <stop offset="100%" stopColor="#F59E0B" />
        </linearGradient>
        <linearGradient id={`trophy-grad-${idSuffix}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"  stopColor="#FCD34D" />
          <stop offset="55%" stopColor="#F59E0B" />
          <stop offset="100%" stopColor="#B45309" />
        </linearGradient>
      </defs>

      {/* Background */}
      <rect width={W} height={H} fill={`url(#bg-${idSuffix})`} rx="60" />
      <rect width={W} height={H} fill={`url(#glow-${idSuffix})`} rx="60" />

      {/* Subtle pattern dots */}
      <g opacity="0.07" fill="#FFFFFF">
        {Array.from({ length: 60 }).map((_, i) => {
          const x = (i * 137) % W;
          const y = ((i * 71) % H);
          return <circle key={i} cx={x} cy={y} r="2" />;
        })}
      </g>

      {/* Border ring */}
      <rect x="36" y="36" width={W - 72} height={H - 72} rx="48" fill="none" stroke={`url(#ring-${idSuffix})`} strokeWidth="2" opacity="0.55" />

      {/* Clinity wordmark + ACHIEVEMENT */}
      <g transform={`translate(${W / 2}, 110)`}>
        <text textAnchor="middle" fontSize="40" fontWeight="700" fill="#C7D2FE" style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.025em' }}>
          Clinity
        </text>
        <text textAnchor="middle" y="34" fontSize="13" fontWeight="700" fill="#94A3B8" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.32em' }}>
          ACHIEVEMENT
        </text>
      </g>

      {/* Trophy circle (compact) */}
      <g transform={`translate(${W / 2}, 290)`}>
        <circle r="74" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.18)" strokeWidth="2" />
        <circle r="66" fill={`url(#trophy-grad-${idSuffix})`} opacity="0.22" />
        <g transform="translate(-36 -36)">
          <path
            d="M62 14H26v22c0 9 5 18 18 18s18-9 18-18V14ZM16 18H6v10c0 7 6 14 14 14v-8c-2 0-4-1-4-4V18ZM72 18v12c0 3-2 4-4 4v8c8 0 14-7 14-14V18H72ZM30 78c0 3 3 6 6 6h16c3 0 6-3 6-6v-6H30v6Z"
            fill={`url(#trophy-grad-${idSuffix})`}
            transform="scale(0.72)"
          />
        </g>
      </g>

      {/* Title — Wellness Champion (largest) */}
      <text
        x={W / 2}
        y="450"
        textAnchor="middle"
        fontSize="78"
        fontWeight="800"
        fill={`url(#text-grad-${idSuffix})`}
        style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.03em' }}
      >
        Wellness Champion
      </text>

      {/* AWARDED TO + Name */}
      {cleanName && (
        <g>
          <text x={W / 2} y="525" textAnchor="middle" fontSize="15" fontWeight="700" fill="#94A3B8" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.36em' }}>
            AWARDED TO
          </text>
          <text
            x={W / 2}
            y="590"
            textAnchor="middle"
            fontSize="52"
            fontWeight="700"
            fill={`url(#name-grad-${idSuffix})`}
            style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.005em' }}
          >
            {cleanName}
          </text>
        </g>
      )}

      {/* Wellness Score */}
      <g transform={`translate(${W / 2}, ${cleanName ? 660 : 600})`}>
        <text textAnchor="middle" fontSize="15" fontWeight="700" fill="#94A3B8" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.36em' }}>
          WELLNESS SCORE
        </text>
      </g>
      <g transform={`translate(${W / 2}, ${cleanName ? 770 : 730})`}>
        <text textAnchor="middle" fontSize="140" fontWeight="800" fill="#FFFFFF" style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.04em' }}>
          {score}
          <tspan fontSize="54" fill="#94A3B8" dy="-28"> /100</tspan>
        </text>
      </g>

      {/* Motivational invitation — drives viewers to take the test */}
      <text
        x={W / 2}
        y="880"
        textAnchor="middle"
        fontSize="26"
        fontWeight="600"
        fill="#E0E7FF"
        style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.005em' }}
      >
        <tspan x={W / 2} dy="0">You know your age.</tspan>
        <tspan x={W / 2} dy="38">Do you know your Wellness Score?</tspan>
      </text>
      <text
        x={W / 2}
        y="956"
        textAnchor="middle"
        fontSize="20"
        fontWeight="600"
        fill="#A5B4FC"
        style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.1em' }}
      >
        TAKE 2 MIN AT clinity.in
      </text>

      {/* Footer divider + tagline */}
      <line x1={W / 2 - 90} y1="998" x2={W / 2 + 90} y2="998" stroke="rgba(199,210,254,0.25)" strokeWidth="1" />
      <text
        x={W / 2}
        y="1034"
        textAnchor="middle"
        fontSize="22"
        fontWeight="600"
        fill="#C7D2FE"
        style={{ fontFamily: 'Outfit, sans-serif', fontStyle: 'italic', letterSpacing: '0.005em' }}
      >
        Navigating Wellness Together™
      </text>
    </svg>
  );
}

/* ---------------- Champion celebration card (score >= 70) ---------------- */
function ChampionCard({ score, dimensionScores, t }) {
  const svgRef = useRef(null);
  const [downloaded, setDownloaded] = useState(false);
  const [name, setName] = useState('');

  const trimmed = name.trim();
  const greet = trimmed ? `I, ${trimmed},` : 'I';

  // Top 2 strongest dimensions become "proof points" in the share message
  const dimLabel = {
    physical: 'Physical', mental: 'Mental', emotional: 'Emotional', financial: 'Financial',
    career: 'Career', lifestyle: 'Lifestyle', spiritual: 'Spiritual', self_care: 'Self-Care',
  };
  const topDims = Object.entries(dimensionScores || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([k, v]) => `${dimLabel[k] || k} ${v}`);
  const highlights = topDims.length ? `\nStrongest: ${topDims.join(' · ')}` : '';

  const shareText = `${greet} scored ${score}/100 on the Clinity Wellness Assessment and earned the Clinity Wellness Champion badge.${highlights}\n\nWhat's your Wellness Score?\n\n#Clinity #WellnessChampion #Wellbeing #NavigatingWellnessTogether`;
  // Referral link — adds ?ref=champion so we can attribute signups back to shares
  const refCode = trimmed ? trimmed.split(/\s+/)[0].toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 12) : 'champion';
  const shareUrl  = typeof window !== 'undefined' ? `${window.location.origin}/?ref=${encodeURIComponent(refCode)}` : `https://clinity.in/?ref=${encodeURIComponent(refCode)}`;

  const downloadBadge = async () => {
    const svgEl = svgRef.current?.querySelector('svg');
    if (!svgEl) return;
    const xml = new XMLSerializer().serializeToString(svgEl);
    const svg64 = btoa(unescape(encodeURIComponent(xml)));
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 1080;
      canvas.height = 1080;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, 1080, 1080);
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `clinity-wellness-champion-${score}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setDownloaded(true);
        setTimeout(() => setDownloaded(false), 2000);
      }, 'image/png');
    };
    img.src = `data:image/svg+xml;base64,${svg64}`;
  };

  const shareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  const shareLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  const shareInstagram = () => {
    // Instagram has no URL share API — download the badge then open Instagram
    downloadBadge();
    setTimeout(() => {
      window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
    }, 600);
  };

  return (
    <div
      className="relative rounded-[28px] overflow-hidden mb-8"
      style={{ background: 'radial-gradient(circle at 50% 0%, #1E1B4B 0%, #0F0F23 80%)' }}
      data-testid="champion-celebration"
    >
      <div
        aria-hidden="true"
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full blur-3xl opacity-50 pointer-events-none"
        style={{ background: 'radial-gradient(rgba(168,85,247,0.45) 0%, transparent 70%)' }}
      />
      {/* Confetti dots */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {Array.from({ length: 14 }).map((_, i) => {
          const colors = ['#A855F7', '#6366F1', '#FBBF24', '#34D399', '#F472B6'];
          const c = colors[i % colors.length];
          const left = `${(i * 73) % 100}%`;
          const top = `${(i * 41) % 90}%`;
          const delay = `${(i * 0.3) % 3}s`;
          return (
            <span
              key={i}
              style={{
                position: 'absolute', left, top, width: 8, height: 8, background: c, borderRadius: 2,
                transform: `rotate(${(i * 37) % 360}deg)`,
                opacity: 0.55,
                animation: `champFloat 4s ease-in-out ${delay} infinite`,
              }}
            />
          );
        })}
      </div>

      <style>{`
        @keyframes champFloat {
          0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.5; }
          50%      { transform: translateY(-14px) rotate(180deg); opacity: 0.9; }
        }
        @keyframes champFadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="relative p-6 sm:p-10 lg:p-12 text-center" style={{ animation: 'champFadeIn 0.7s ease-out both' }}>
        {/* Congrats */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5"
             style={{ background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.3)' }}>
          <Sparkles className="w-3.5 h-3.5 text-[#C4B5FD]" />
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#E0E7FF]">{t('champion.congrats.eyebrow')}</span>
        </div>

        <h2
          className="text-3xl sm:text-5xl lg:text-[58px] font-extrabold text-white mb-3 leading-[1.05]"
          style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.035em' }}
          data-testid="champion-title"
        >
          {t('champion.celebration.title')}
        </h2>
        <p className="text-base sm:text-lg text-[#C7D2FE] max-w-2xl mx-auto mb-2">
          {t('champion.celebration.subtitle')}
        </p>
        <p className="text-sm sm:text-[15px] text-[#94A3B8] max-w-2xl mx-auto mb-8 leading-[1.65]">
          {t('champion.celebration.description')}
        </p>

        {/* Name input — personalizes the badge in real-time */}
        <div className="max-w-sm mx-auto mb-7">
          <label
            htmlFor="champion-name-input"
            className="block text-[11px] font-bold uppercase tracking-[0.22em] text-[#A5B4FC] mb-2.5"
          >
            {t('champion.name.label')}
          </label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              id="champion-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('champion.name.placeholder')}
              maxLength={30}
              data-testid="champion-name-input"
              className="w-full pl-11 pr-4 py-3 rounded-full text-[15px] font-medium text-white placeholder:text-white/30 focus:outline-none transition-all"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(199,210,254,0.2)',
                backdropFilter: 'blur(8px)',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(168,85,247,0.6)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(199,210,254,0.2)'; }}
            />
          </div>
          <p className="text-[11px] text-white/45 mt-2">{t('champion.name.hint')}</p>
        </div>

        {/* The Badge — visible at ~ 320px wide on desktop, scales down on mobile */}
        <div
          className="mx-auto mb-8 rounded-3xl overflow-hidden"
          ref={svgRef}
          style={{
            width: 'min(360px, 100%)',
            aspectRatio: '1 / 1',
            boxShadow: '0 40px 80px -20px rgba(0,0,0,0.6), 0 0 60px rgba(168,85,247,0.25)',
          }}
          data-testid="champion-badge"
        >
          <BadgeSVG score={score} name={trimmed} idSuffix="live" />
        </div>

        {/* Share row */}
        <div className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-3 mb-5">
          <ShareBtn icon={Instagram} label={t('champion.share.instagram')} onClick={shareInstagram} accent="linear-gradient(135deg,#FFB347 0%, #EA487A 50%, #B43FBA 100%)" testid="share-instagram" />
          <ShareBtn icon={MessageCircle} label={t('champion.share.whatsapp')} onClick={shareWhatsApp} accent="linear-gradient(135deg, #25D366 0%, #128C7E 100%)" testid="share-whatsapp" />
          <ShareBtn icon={Linkedin} label={t('champion.share.linkedin')} onClick={shareLinkedIn} accent="linear-gradient(135deg,#0A66C2 0%, #084482 100%)" testid="share-linkedin" />
          <ShareBtn icon={downloaded ? Check : Download} label={downloaded ? t('champion.share.downloaded') : t('champion.share.download')} onClick={downloadBadge} accent="linear-gradient(135deg,#6366F1 0%, #8B5CF6 50%, #A855F7 100%)" testid="share-download" />
        </div>

        <p className="text-[12px] text-white/45 flex items-center justify-center gap-2">
          <Share2 className="w-3.5 h-3.5" />
          {t('champion.share.hint')}
        </p>
      </div>
    </div>
  );
}

function ShareBtn({ icon: Icon, label, onClick, accent, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="group inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[13px] font-semibold text-white no-underline transition-all duration-300 hover:-translate-y-0.5"
      style={{
        background: accent,
        boxShadow: '0 10px 24px -10px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.18)',
      }}
    >
      <Icon className="w-[15px] h-[15px]" strokeWidth={2.2} />
      {label}
    </button>
  );
}

/* ---------------- Progress card (score < 70) ---------------- */
function ProgressCard({ score, stage, t }) {
  return (
    <div
      className="relative rounded-3xl overflow-hidden mb-8 border border-indigo-100"
      style={{
        background: 'linear-gradient(135deg, #F8FAFC 0%, #EEF2FF 60%, #F5F3FF 100%)',
        boxShadow: '0 24px 60px -20px rgba(79,70,229,0.18)',
      }}
      data-testid="champion-progress"
    >
      <div className="grid md:grid-cols-[1fr,auto] gap-8 p-7 sm:p-10 items-center">
        <div>
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="text-2xl">{stage.emoji}</span>
            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#4F46E5]">{t('champion.progress.eyebrow')}</span>
          </div>
          <h3
            className="text-2xl sm:text-[32px] font-bold text-[#0F172A] leading-tight mb-3"
            style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.022em' }}
            data-testid="progress-title"
          >
            {t(stage.titleKey)}
          </h3>
          <p className="text-[15px] text-[#475569] leading-[1.7] mb-5 max-w-xl">{t(stage.bodyKey)}</p>
          <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#64748B]">
            <Lock className="w-3.5 h-3.5 text-[#94A3B8]" />
            <span>{t('champion.locked.hint')}</span>
          </div>
        </div>

        {/* Locked badge preview — small, muted, with lock overlay */}
        <div className="relative mx-auto" style={{ width: 200, aspectRatio: '1 / 1' }}>
          <div
            className="absolute inset-0 rounded-2xl overflow-hidden"
            style={{
              filter: 'grayscale(0.6) brightness(0.85)',
              opacity: 0.78,
              boxShadow: '0 20px 40px -16px rgba(15,23,42,0.25)',
            }}
            aria-hidden="true"
          >
            <BadgeSVG score={score} name="" idSuffix="locked" />
          </div>
          {/* Lock overlay */}
          <div className="absolute inset-0 rounded-2xl flex items-center justify-center bg-black/35 backdrop-blur-[2px]">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(255,255,255,0.95)', boxShadow: '0 10px 24px -10px rgba(0,0,0,0.4)' }}
            >
              <Lock className="w-6 h-6 text-[#4F46E5]" strokeWidth={2.4} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Main exported component ---------------- */
export default function WellnessChampion({ score, dimensionScores }) {
  const { t } = useTranslation();
  if (typeof score !== 'number') return null;
  const stage = getStage(score);

  if (stage.unlocked) {
    return <ChampionCard score={score} dimensionScores={dimensionScores} t={t} />;
  }
  return <ProgressCard score={score} stage={stage} t={t} />;
}

/* ---------------- Landing-page preview ---------------- */
export function ChampionPreviewBadge({ score = 82, name = 'Your Name' }) {
  return (
    <div
      className="mx-auto rounded-3xl overflow-hidden"
      style={{
        width: 'min(340px, 100%)',
        aspectRatio: '1 / 1',
        boxShadow: '0 30px 70px -20px rgba(15,23,42,0.45), 0 0 60px rgba(168,85,247,0.18)',
      }}
      data-testid="landing-champion-preview"
    >
      <BadgeSVG score={score} name={name} idSuffix="preview" />
    </div>
  );
}
